import React, { useEffect, useState } from 'react';
import { FaCheck, FaTrashAlt } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { getPlanComptes, createPlanCompte, getEcritures, createEcriture, deleteEcriture, validerEcriture, getBalance, getGrandLivre, getStations } from '../services/api';

const JOURNAL_LABELS: Record<string, string> = { VE: 'Ventes', AC: 'Achats', BQ: 'Banque', CA: 'Caisse', OD: 'Operations Diverses' };
const CLASSE_LABELS: Record<number, string> = { 1: 'Capitaux', 2: 'Immobilisations', 3: 'Stocks', 4: 'Tiers', 5: 'Tresorerie', 6: 'Charges', 7: 'Produits' };

const Comptabilite: React.FC = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState<'ecritures' | 'plan' | 'balance' | 'grandlivre'>('ecritures');
  const [grandLivre, setGrandLivre] = useState<any>(null);
  const [glCompteId, setGlCompteId] = useState('');
  const [ecritures, setEcritures] = useState<any[]>([]);
  const [comptes, setComptes] = useState<any[]>([]);
  const [balance, setBalance] = useState<any[]>([]);
  const [stations, setStations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showCompteModal, setShowCompteModal] = useState(false);
  const [filters, setFilters] = useState({ station_id: '', date_debut: '', date_fin: '', compte: '' });
  const [form, setForm] = useState({ date_ecriture: new Date().toISOString().slice(0, 10), compte_id: '', libelle: '', debit: '', credit: '', piece_ref: '', station_id: '', journal: 'OD' });
  const [compteForm, setCompteForm] = useState({ numero: '', libelle: '', classe: '6', type_compte: 'detail' });
  const [msg, setMsg] = useState('');

  const isGlobal = user && ['superadmin', 'admin_siege', 'comptable', 'financier'].includes(user.role);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [cRes, sRes] = await Promise.all([getPlanComptes({}), getStations()]);
      setComptes(cRes.data);
      setStations(sRes.data);
      await loadEcritures();
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const loadEcritures = async () => {
    try {
      const params: any = {};
      if (filters.station_id) params.station_id = filters.station_id;
      if (filters.date_debut) params.date_debut = filters.date_debut;
      if (filters.date_fin) params.date_fin = filters.date_fin;
      if (filters.compte) params.compte = filters.compte;
      const res = await getEcritures(params);
      setEcritures(res.data);
    } catch (err) { console.error(err); }
  };

  const loadBalance = async () => {
    try {
      const params: any = {};
      if (filters.station_id) params.station_id = filters.station_id;
      if (filters.date_debut) params.date_debut = filters.date_debut;
      if (filters.date_fin) params.date_fin = filters.date_fin;
      const res = await getBalance(params);
      setBalance(res.data);
    } catch (err) { console.error(err); }
  };

  useEffect(() => {
    if (tab === 'ecritures') loadEcritures();
    if (tab === 'balance') loadBalance();
    if (tab === 'grandlivre' && glCompteId) loadGrandLivre();
    if (tab === 'plan') { /* already loaded */ }
  }, [tab, filters, glCompteId]);

  const loadGrandLivre = async () => {
    if (!glCompteId) return;
    try {
      const params: any = { compte_id: glCompteId };
      if (filters.station_id) params.station_id = filters.station_id;
      if (filters.date_debut) params.date_debut = filters.date_debut;
      if (filters.date_fin) params.date_fin = filters.date_fin;
      const res = await getGrandLivre(params);
      setGrandLivre(res.data);
    } catch (err) { console.error(err); }
  };

  const handleCreateEcriture = async () => {
    setMsg('');
    try {
      await createEcriture({
        ...form,
        compte_id: Number(form.compte_id),
        debit: Number(form.debit) || 0,
        credit: Number(form.credit) || 0,
        station_id: form.station_id ? Number(form.station_id) : null
      });
      setMsg('Ecriture creee');
      setShowModal(false);
      setForm({ date_ecriture: new Date().toISOString().slice(0, 10), compte_id: '', libelle: '', debit: '', credit: '', piece_ref: '', station_id: '', journal: 'OD' });
      loadEcritures();
    } catch (err: any) {
      setMsg(err.response?.data?.error || 'Erreur');
    }
  };

  const handleCreateCompte = async () => {
    try {
      await createPlanCompte({ ...compteForm, classe: Number(compteForm.classe) });
      setShowCompteModal(false);
      setCompteForm({ numero: '', libelle: '', classe: '6', type_compte: 'detail' });
      const res = await getPlanComptes({});
      setComptes(res.data);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Supprimer cette ecriture ?')) return;
    try {
      await deleteEcriture(id);
      loadEcritures();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  const handleValider = async (id: number) => {
    try {
      await validerEcriture(id);
      loadEcritures();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  const fmt = (n: number) => n.toLocaleString('fr-FR', { minimumFractionDigits: 0 });

  return (
    <div>
      <div className="page-header">
        <h2>Comptabilite</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          {tab === 'ecritures' && isGlobal && <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ Nouvelle ecriture</button>}
          {tab === 'plan' && isGlobal && <button className="btn btn-primary" onClick={() => setShowCompteModal(true)}>+ Nouveau compte</button>}
        </div>
      </div>

      {msg && <div className="alert alert-success">{msg}</div>}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '2px solid #eee' }}>
        {[{k:'ecritures',l:'Journal Comptable'},{k:'plan',l:'Plan de Comptes'},{k:'balance',l:'Balance'},{k:'grandlivre',l:'Grand Livre'}].map(t => (
          <button key={t.k} onClick={() => setTab(t.k as any)}
            style={{ padding: '10px 24px', border: 'none', background: tab === t.k ? '#004A97' : 'transparent', color: tab === t.k ? '#fff' : '#666', cursor: 'pointer', fontWeight: tab === t.k ? 600 : 400, borderRadius: '6px 6px 0 0', fontSize: 14 }}>
            {t.l}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="filters" style={{ marginBottom: 16 }}>
        {isGlobal && (
          <select value={filters.station_id} onChange={e => setFilters({ ...filters, station_id: e.target.value })}>
            <option value="">Toutes les stations</option>
            {stations.map((s: any) => <option key={s.id} value={s.id}>{s.nom}</option>)}
          </select>
        )}
        <input type="date" value={filters.date_debut} onChange={e => setFilters({ ...filters, date_debut: e.target.value })} />
        <input type="date" value={filters.date_fin} onChange={e => setFilters({ ...filters, date_fin: e.target.value })} />
      </div>

      {loading ? <div className="loading">Chargement...</div> : (
        <>
          {/* Journal Comptable */}
          {tab === 'ecritures' && (
            <table className="table">
              <thead>
                <tr><th>Date</th><th>Journal</th><th>Compte</th><th>Libelle</th><th>Debit</th><th>Credit</th><th>Ref</th><th>Station</th><th>Statut</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {ecritures.map((e: any) => (
                  <tr key={e.id}>
                    <td>{new Date(e.date_ecriture).toLocaleDateString('fr-FR')}</td>
                    <td><span className="badge">{JOURNAL_LABELS[e.journal] || e.journal}</span></td>
                    <td><strong>{e.compte_numero}</strong> {e.compte_libelle}</td>
                    <td>{e.libelle}</td>
                    <td style={{ textAlign: 'right', color: e.debit > 0 ? '#004A97' : '#999' }}>{e.debit > 0 ? fmt(e.debit) : '-'}</td>
                    <td style={{ textAlign: 'right', color: e.credit > 0 ? '#ED0000' : '#999' }}>{e.credit > 0 ? fmt(e.credit) : '-'}</td>
                    <td>{e.piece_ref || '-'}</td>
                    <td>{e.station_nom || '-'}</td>
                    <td><span className={`badge badge-${e.validee ? 'success' : 'warning'}`}>{e.validee ? 'Validee' : 'Brouillon'}</span></td>
                    <td>
                      {!e.validee && isGlobal && (
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="btn btn-sm btn-success" onClick={() => handleValider(e.id)} title="Valider"><FaCheck /></button>
                          <button className="btn btn-sm btn-danger" onClick={() => handleDelete(e.id)} title="Supprimer"><FaTrashAlt /></button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {ecritures.length === 0 && <tr><td colSpan={10} style={{ textAlign: 'center', padding: 30 }}>Aucune ecriture</td></tr>}
              </tbody>
              {ecritures.length > 0 && (
                <tfoot>
                  <tr style={{ fontWeight: 700, background: '#f8f9fa' }}>
                    <td colSpan={4}>Total</td>
                    <td style={{ textAlign: 'right' }}>{fmt(ecritures.reduce((s: number, e: any) => s + Number(e.debit), 0))}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(ecritures.reduce((s: number, e: any) => s + Number(e.credit), 0))}</td>
                    <td colSpan={4}></td>
                  </tr>
                </tfoot>
              )}
            </table>
          )}

          {/* Plan de comptes */}
          {tab === 'plan' && (
            <table className="table">
              <thead>
                <tr><th>Numero</th><th>Libelle</th><th>Classe</th><th>Type</th></tr>
              </thead>
              <tbody>
                {comptes.map((c: any) => (
                  <tr key={c.id} style={{ fontWeight: c.type_compte === 'racine' ? 700 : 400, background: c.type_compte === 'racine' ? '#f0f4ff' : 'transparent' }}>
                    <td>{c.numero}</td>
                    <td>{c.type_compte === 'detail' ? '\u00A0\u00A0\u00A0\u00A0' : ''}{c.libelle}</td>
                    <td><span className="badge badge-info">{c.classe} - {CLASSE_LABELS[c.classe] || ''}</span></td>
                    <td>{c.type_compte}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Balance */}
          {tab === 'balance' && (
            <table className="table">
              <thead>
                <tr><th>Numero</th><th>Libelle</th><th>Classe</th><th style={{ textAlign: 'right' }}>Total Debit</th><th style={{ textAlign: 'right' }}>Total Credit</th><th style={{ textAlign: 'right' }}>Solde</th></tr>
              </thead>
              <tbody>
                {balance.map((b: any) => (
                  <tr key={b.id}>
                    <td><strong>{b.numero}</strong></td>
                    <td>{b.libelle}</td>
                    <td>{b.classe} - {CLASSE_LABELS[b.classe] || ''}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(Number(b.total_debit))}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(Number(b.total_credit))}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600, color: Number(b.solde) >= 0 ? '#004A97' : '#ED0000' }}>{fmt(Number(b.solde))}</td>
                  </tr>
                ))}
                {balance.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', padding: 30 }}>Aucune donnee</td></tr>}
              </tbody>
              {balance.length > 0 && (
                <tfoot>
                  <tr style={{ fontWeight: 700, background: '#f8f9fa' }}>
                    <td colSpan={3}>Total</td>
                    <td style={{ textAlign: 'right' }}>{fmt(balance.reduce((s: number, b: any) => s + Number(b.total_debit), 0))}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(balance.reduce((s: number, b: any) => s + Number(b.total_credit), 0))}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(balance.reduce((s: number, b: any) => s + Number(b.solde), 0))}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          )}

          {/* Grand Livre */}
          {tab === 'grandlivre' && (
            <div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontWeight: 600, marginRight: 8 }}>Compte:</label>
                <select value={glCompteId} onChange={e => setGlCompteId(e.target.value)} style={{ minWidth: 300 }}>
                  <option value="">-- Selectionner un compte --</option>
                  {comptes.filter((c: any) => c.type_compte === 'detail').map((c: any) => (
                    <option key={c.id} value={c.id}>{c.numero} - {c.libelle}</option>
                  ))}
                </select>
              </div>
              {grandLivre && (
                <>
                  <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                    <div className="card" style={{ padding: 14, textAlign: 'center', flex: 1 }}>
                      <div style={{ fontSize: 11, color: '#888' }}>TOTAL DEBIT</div>
                      <div style={{ fontSize: 20, fontWeight: 700, color: '#004A97' }}>{fmt(grandLivre.totalDebit)}</div>
                    </div>
                    <div className="card" style={{ padding: 14, textAlign: 'center', flex: 1 }}>
                      <div style={{ fontSize: 11, color: '#888' }}>TOTAL CREDIT</div>
                      <div style={{ fontSize: 20, fontWeight: 700, color: '#ED0000' }}>{fmt(grandLivre.totalCredit)}</div>
                    </div>
                    <div className="card" style={{ padding: 14, textAlign: 'center', flex: 1 }}>
                      <div style={{ fontSize: 11, color: '#888' }}>SOLDE</div>
                      <div style={{ fontSize: 20, fontWeight: 700, color: grandLivre.solde >= 0 ? '#78BE20' : '#ED0000' }}>{fmt(grandLivre.solde)}</div>
                    </div>
                  </div>
                  <table className="table">
                    <thead><tr><th>Date</th><th>Libelle</th><th>Ref</th><th style={{ textAlign: 'right' }}>Debit</th><th style={{ textAlign: 'right' }}>Credit</th><th style={{ textAlign: 'right' }}>Solde cumule</th></tr></thead>
                    <tbody>
                      {(() => { let cumul = 0; return grandLivre.ecritures.map((e: any) => { cumul += Number(e.debit) - Number(e.credit); return (
                        <tr key={e.id}>
                          <td>{new Date(e.date_ecriture).toLocaleDateString('fr-FR')}</td>
                          <td>{e.libelle}</td>
                          <td>{e.piece_ref || '-'}</td>
                          <td style={{ textAlign: 'right' }}>{Number(e.debit) > 0 ? fmt(Number(e.debit)) : '-'}</td>
                          <td style={{ textAlign: 'right' }}>{Number(e.credit) > 0 ? fmt(Number(e.credit)) : '-'}</td>
                          <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(cumul)}</td>
                        </tr>
                      ); }); })()}
                      {grandLivre.ecritures.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', padding: 30 }}>Aucune ecriture pour ce compte</td></tr>}
                    </tbody>
                  </table>
                </>
              )}
              {!glCompteId && <div style={{ padding: 30, textAlign: 'center', color: '#999' }}>Selectionnez un compte pour afficher le grand livre</div>}
            </div>
          )}
        </>
      )}

      {/* Modal nouvelle ecriture */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
            <div className="modal-header">
              <h3>Nouvelle ecriture comptable</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label>Date</label>
                  <input type="date" value={form.date_ecriture} onChange={e => setForm({...form, date_ecriture: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Journal</label>
                  <select value={form.journal} onChange={e => setForm({...form, journal: e.target.value})}>
                    {Object.entries(JOURNAL_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>Compte</label>
                  <select value={form.compte_id} onChange={e => setForm({...form, compte_id: e.target.value})}>
                    <option value="">-- Selectionner --</option>
                    {comptes.filter((c: any) => c.type_compte === 'detail').map((c: any) => (
                      <option key={c.id} value={c.id}>{c.numero} - {c.libelle}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>Libelle</label>
                  <input type="text" value={form.libelle} onChange={e => setForm({...form, libelle: e.target.value})} placeholder="Description de l'ecriture" />
                </div>
                <div className="form-group">
                  <label>Debit (FCFA)</label>
                  <input type="number" value={form.debit} onChange={e => setForm({...form, debit: e.target.value, credit: ''})} placeholder="0" />
                </div>
                <div className="form-group">
                  <label>Credit (FCFA)</label>
                  <input type="number" value={form.credit} onChange={e => setForm({...form, credit: e.target.value, debit: ''})} placeholder="0" />
                </div>
                <div className="form-group">
                  <label>Ref. Piece</label>
                  <input type="text" value={form.piece_ref} onChange={e => setForm({...form, piece_ref: e.target.value})} placeholder="FAC-001" />
                </div>
                <div className="form-group">
                  <label>Station</label>
                  <select value={form.station_id} onChange={e => setForm({...form, station_id: e.target.value})}>
                    <option value="">-- Aucune --</option>
                    {stations.map((s: any) => <option key={s.id} value={s.id}>{s.nom}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Annuler</button>
              <button className="btn btn-primary" onClick={handleCreateEcriture} disabled={!form.compte_id || !form.libelle}>Enregistrer</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal nouveau compte */}
      {showCompteModal && (
        <div className="modal-overlay" onClick={() => setShowCompteModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
            <div className="modal-header">
              <h3>Nouveau compte</h3>
              <button className="modal-close" onClick={() => setShowCompteModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Numero</label>
                <input type="text" value={compteForm.numero} onChange={e => setCompteForm({...compteForm, numero: e.target.value})} placeholder="Ex: 605" />
              </div>
              <div className="form-group">
                <label>Libelle</label>
                <input type="text" value={compteForm.libelle} onChange={e => setCompteForm({...compteForm, libelle: e.target.value})} placeholder="Nom du compte" />
              </div>
              <div className="form-group">
                <label>Classe</label>
                <select value={compteForm.classe} onChange={e => setCompteForm({...compteForm, classe: e.target.value})}>
                  {Object.entries(CLASSE_LABELS).map(([k, v]) => <option key={k} value={k}>{k} - {v}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Type</label>
                <select value={compteForm.type_compte} onChange={e => setCompteForm({...compteForm, type_compte: e.target.value})}>
                  <option value="detail">Detail</option>
                  <option value="racine">Racine</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowCompteModal(false)}>Annuler</button>
              <button className="btn btn-primary" onClick={handleCreateCompte} disabled={!compteForm.numero || !compteForm.libelle}>Creer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Comptabilite;
